// These calls bypass supabase-js on purpose (binary downloads / streaming);
// the timeout policy must not bypass with them.
import { fetchWithTimeout } from "@/lib/supabaseFetch";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  briefingDayDoorsQuery,
  briefingStagesQuery,
  briefingViewQuery,
  byArrival,
  crewMembersQuery,
} from "@/lib/queries/productionQueries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Clock, Download, ChevronDown } from "lucide-react";
import {
  isCameraIdRole,
  normalizeCameraId,
  cameraSettingsText,
  formatDoorsRange,
} from "@produce/shared";
import ProductionSidebar from "@/components/ProductionSidebar";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import { useToast } from "@/hooks/use-toast";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { useState } from "react";
import type { BriefingStage, CateringMealConfig, Restrictions } from "@/types/domain";
import { parseStages, parseCateringEntries } from "@/types/domain";
import { stagesForDay } from "@/lib/briefingStages";
import { DayTabs } from "@/components/briefing/DayTabs";
import { CustomFieldsCard } from "@/components/briefing/CustomFieldsCard";
import { FlightsCard } from "@/components/briefing/FlightsCard";
import { VideoExamplesCard } from "@/components/briefing/VideoExamplesCard";
import { useFestivalDays } from "@/hooks/useFestivalDays";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import { useAuth } from "@/contexts/AuthContext";
import { reconstructFestivalDays } from "@/lib/doorsForDay";
import { useFestivalMapUrl } from "@/hooks/useFestivalMapUrl";
import { WeatherCard } from "@/components/WeatherCard";
import { format, parseISO } from "date-fns";

const BriefingView = () => {
  const { productionId } = useParams();
  const navigate = useNavigate();
  const { expanded } = useSidebar();
  const { toast } = useToast();
  const { session } = useAuth();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  // The producer's own 12h/24h schedule setting. This screen carried a
  // private 24h copy, so the setting did nothing here.
  // Up here with the other hooks: this component returns early while the
  // briefing loads, and a hook below that would not run on those renders.
  const formatTime = useProducerTimeFormatter();

  // Fetch production/briefing details
  const { data: briefing, isLoading } = useQuery({
    ...briefingViewQuery(productionId),
    enabled: !!productionId,
  });

  const mapDisplayUrl = useFestivalMapUrl(briefing);

  // Per-day doors overrides for multi-day briefings.
  const { data: dayDoors = [] } = useQuery({
    ...briefingDayDoorsQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch crew members for arrival times
  const { data: crewMembers = [] } = useQuery({
    ...crewMembersQuery(productionId),
    enabled: !!productionId,
    // Same rows as everywhere else, just in arrival order for this table.
    select: byArrival,
  });

  // Camera operators & photographers — the only crew with a Camera ID.
  const cameraCrew = crewMembers.filter((m) => isCameraIdRole(m.function));

  // Canonical per-day stages for the multi-day day selector.
  const { data: briefingStages = [] } = useQuery({
    ...briefingStagesQuery(productionId),
    enabled: !!productionId,
  });

  // Day list + selected day for the Day 1 / Day 2 / Day 3 selectors that
  // split multi-day stages, crew and Camera IDs per day.
  const { festivalDates, isMultiDay, selectedDate, setSelectedDate } = useFestivalDays(
    briefing,
    dayDoors,
  );

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);

      const response = await fetchWithTimeout(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-briefing-pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ briefingId: productionId }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PROduce - ${briefing?.festival_name || "Briefing"} - Briefing.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Success",
        description: "Briefing PDF downloaded successfully",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to download PDF",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <ProductionSidebar productionId={productionId!} />
        <main className="ml-20 flex flex-1 items-center justify-center bg-background p-8">
          <div className="text-muted-foreground">Loading briefing...</div>
        </main>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="flex min-h-screen">
        <ProductionSidebar productionId={productionId!} />
        <main className="ml-20 flex flex-1 items-center justify-center bg-background p-8">
          <div className="text-center">
            <h2 className="mb-2 text-xl font-semibold">Briefing not found</h2>
          </div>
        </main>
      </div>
    );
  }

  const isSingleDay = briefing.festival_date_start === briefing.festival_date_end;
  const cameraText = cameraSettingsText(briefing.camera_settings);

  return (
    <div className="flex min-h-screen bg-background">
      <ProductionSidebar productionId={productionId!} />

      <PageTransition
        className="flex-1 overflow-auto transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        <div className="border-b border-border bg-background px-6 py-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{briefing.festival_name || "Briefing"}</h1>
              <p className="text-sm text-muted-foreground">
                Production briefing and crew information
              </p>
            </div>
            <div className="flex items-center gap-3">
              {briefing.status === "published" && (
                <Button variant="outline" onClick={handleDownloadPDF} disabled={isGeneratingPDF}>
                  <Download className="mr-2 h-4 w-4" />
                  {isGeneratingPDF ? "Generating..." : "Download PDF"}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    Edit briefing
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Quick edit</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=0&shortcut=true`)}
                  >
                    Basic information
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=1&shortcut=true`)}
                  >
                    Stages per day
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=2&shortcut=true`)}
                  >
                    Timetable import
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=3&shortcut=true`)}
                  >
                    The crew
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=4&shortcut=true`)}
                  >
                    Festival map
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=5&shortcut=true`)}
                  >
                    Crew schedules
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=6&shortcut=true`)}
                  >
                    General briefing
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=7&shortcut=true`)}
                  >
                    Live tracking setup
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit?step=8&shortcut=true`)}
                  >
                    Review & publish
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate(`/briefings/${productionId}/edit`)}
                    className="font-medium"
                  >
                    Open full editor
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Badge
                variant={briefing.status === "published" ? "default" : "secondary"}
                className="px-3 py-1 text-sm"
              >
                {briefing.status === "published" ? "Published" : "Draft"}
              </Badge>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="mx-auto max-w-5xl">
            {/* Summary Cards */}
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card className="bg-card p-6">
                <div className="mb-3 flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Event dates</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {briefing.festival_date_start && briefing.festival_date_end && !isSingleDay
                    ? `${new Date(briefing.festival_date_start).toLocaleDateString()} - ${new Date(briefing.festival_date_end).toLocaleDateString()}`
                    : briefing.festival_date_start
                      ? new Date(briefing.festival_date_start).toLocaleDateString()
                      : "Not set"}
                </p>
              </Card>

              <Card className="bg-card p-6">
                <div className="mb-3 flex items-center gap-3">
                  <Clock className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Doors</h3>
                </div>
                {(() => {
                  const days =
                    briefing.festival_date_start && briefing.festival_date_end
                      ? reconstructFestivalDays({
                          startDate: briefing.festival_date_start,
                          endDate: briefing.festival_date_end,
                          perDayDoors: dayDoors,
                          fallback: {
                            doors_open: briefing.doors_open ?? null,
                            doors_close: briefing.doors_close ?? null,
                          },
                        })
                      : [];
                  const anySet = days.some((d) => d.startTime || d.endTime);
                  if (days.length === 0 || !anySet)
                    return <p className="text-sm text-muted-foreground">Not set</p>;
                  if (days.length === 1)
                    return (
                      <p className="text-sm text-muted-foreground">
                        {formatDoorsRange(days[0], formatTime)}
                      </p>
                    );
                  return (
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {days.map((d) => (
                        <li key={d.date}>
                          {format(parseISO(d.date), "EEE d MMM")}:{" "}
                          {d.startTime || d.endTime ? formatDoorsRange(d, formatTime) : "Not set"}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </Card>

              <Card className="bg-card p-6">
                <div className="mb-3 flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Navigation address</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {briefing.navigation_address || "Not set"}
                </p>
              </Card>
            </div>

            {/* Producer information — same fields the crew app shows, read
                from the producer's profile via fetchBriefingDetail. */}
            {briefing.producer &&
              (() => {
                const p = briefing.producer;
                const rows = [
                  { label: "Company", value: p.company_name },
                  { label: "Producer", value: p.full_name },
                  { label: "Phone", value: p.phone, href: p.phone && `tel:${p.phone}` },
                  { label: "Email", value: p.email, href: p.email && `mailto:${p.email}` },
                  {
                    label: "Invoice information",
                    value: p.invoice_information && String(p.invoice_information),
                    wide: true,
                  },
                ].filter((r) => r.value);
                if (rows.length === 0) return null;
                return (
                  <Card className="mb-6 bg-card p-8">
                    <h2 className="mb-6 text-2xl font-bold">Producer information</h2>
                    <div className="grid grid-cols-2 gap-6">
                      {rows.map((r) => (
                        <div key={r.label} className={r.wide ? "col-span-2" : undefined}>
                          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                            {r.label}
                          </h3>
                          {r.href ? (
                            <a href={r.href} className="text-primary underline">
                              {r.value}
                            </a>
                          ) : (
                            <p
                              className={
                                r.wide ? "whitespace-pre-wrap text-foreground" : "text-foreground"
                              }
                            >
                              {r.value}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })()}

            {/* General Briefing Information */}
            <Card className="mb-6 bg-card p-8">
              <h2 className="mb-6 text-2xl font-bold">General briefing information</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Location</h3>
                    <p className="text-foreground">{briefing.location || "Not set"}</p>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Navigation address
                    </h3>
                    <p className="text-foreground">{briefing.navigation_address || "Not set"}</p>
                  </div>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Hotel address
                    </h3>
                    <p className="text-foreground">{briefing.hotel_address || "Not set"}</p>
                  </div>
                </div>

                {(briefingStages.length > 0 ||
                  (briefing.stages && parseStages(briefing.stages).length > 0)) &&
                  (() => {
                    // Per-day stages from briefing_stages (no duplicates);
                    // legacy JSONB fallback for old single-day briefings.
                    const labels =
                      briefingStages.length > 0
                        ? stagesForDay(briefingStages, selectedDate).map((s) => s.label)
                        : parseStages(briefing.stages).map((s: BriefingStage) => s.label || s.name);
                    return (
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Stages</h3>
                        <DayTabs
                          dates={festivalDates}
                          selectedDate={selectedDate}
                          onSelect={setSelectedDate}
                          dayNames={briefing.day_names}
                        />
                        <div className="flex flex-wrap gap-2">
                          {labels.length > 0 ? (
                            labels.map((label, idx) => (
                              <Badge key={idx} variant="outline">
                                {label}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">No stages for this day.</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {briefing.crew_checkin && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Crew check-in
                    </h3>
                    <p className="text-foreground">{briefing.crew_checkin}</p>
                  </div>
                )}

                {briefing.crew_dresscode && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Crew dresscode
                    </h3>
                    <p className="text-foreground">{briefing.crew_dresscode}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Weather forecast — under General, above Catering. Collapsible
                per day (remembered) so multi-day forecasts stay compact. */}
            <div className="mb-6">
              <WeatherCard
                briefingId={productionId}
                perDayDoors={dayDoors}
                fallbackDoors={{
                  doors_open: briefing.doors_open ?? null,
                  doors_close: briefing.doors_close ?? null,
                }}
                collapsible
                variant="page"
              />
            </div>

            {/* Catering */}
            {briefing.catering && Object.keys(briefing.catering as object).length > 0 && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Catering</h2>
                <div className="grid grid-cols-2 gap-6">
                  {parseCateringEntries(briefing.catering).map(
                    ([key, value]: [string, CateringMealConfig]) =>
                      value &&
                      value.start && (
                        <div key={key}>
                          <h3 className="mb-2 text-sm font-semibold capitalize text-muted-foreground">
                            {key}
                          </h3>
                          <p className="text-foreground">
                            {value.start} - {value.end}
                          </p>
                        </div>
                      ),
                  )}
                </div>
              </Card>
            )}

            {/* Technical Settings */}
            {(cameraText || briefing.audio_settings || briefing.extra_settings) && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Technical settings</h2>
                <div className="space-y-6">
                  {cameraText && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        Camera settings
                      </h3>
                      <p className="whitespace-pre-wrap text-foreground">{cameraText}</p>
                    </div>
                  )}

                  {briefing.audio_settings && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        Audio settings
                      </h3>
                      <p className="text-foreground">{briefing.audio_settings}</p>
                    </div>
                  )}

                  {briefing.extra_settings && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        Extra settings
                      </h3>
                      <p className="text-foreground">{briefing.extra_settings}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Restrictions & Instructions */}
            {(briefing.restrictions || briefing.general_instructions || briefing.others) && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Restrictions & instructions</h2>
                <div className="space-y-6">
                  {briefing.restrictions &&
                    typeof briefing.restrictions === "object" &&
                    Object.keys(briefing.restrictions as object).length > 0 &&
                    (briefing.restrictions as Restrictions).text && (
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                          Restrictions
                        </h3>
                        <p className="whitespace-pre-wrap text-foreground">
                          {(briefing.restrictions as Restrictions).text}
                        </p>
                      </div>
                    )}

                  {briefing.general_instructions && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        General instructions
                      </h3>
                      <p className="whitespace-pre-wrap text-foreground">
                        {briefing.general_instructions}
                      </p>
                    </div>
                  )}

                  {briefing.others && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        Other notes
                      </h3>
                      <p className="whitespace-pre-wrap text-foreground">{briefing.others}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Flights, grouped per crew member. Producer-only surface:
                crew read their own flights in the app. */}
            <FlightsCard briefingId={briefing.id} enabled={!!briefing.flights_enabled} />

            <VideoExamplesCard videoExamples={briefing.video_examples} />

            {/* Producer-defined extra fields */}
            <CustomFieldsCard customFields={briefing.custom_fields} />

            {/* Festival Map */}
            {mapDisplayUrl && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Festival map</h2>
                <img
                  src={mapDisplayUrl}
                  alt="Festival map"
                  className="max-h-[500px] w-full rounded-lg object-contain"
                />
              </Card>
            )}

            {/* Crew Arrival Times */}
            {crewMembers.length > 0 && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Crew arrival times</h2>
                <DayTabs
                  dates={festivalDates}
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                  dayNames={briefing.day_names}
                />
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Function
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Arrival time
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isMultiDay
                        ? crewMembers.filter((m) => m.event_date === selectedDate)
                        : crewMembers
                      ).map((member) => (
                        <tr key={member.id} className="border-b border-border/50">
                          <td className="px-4 py-3 text-foreground">{member.name}</td>
                          <td className="px-4 py-3 text-foreground">{member.function}</td>
                          <td className="px-4 py-3 text-foreground">
                            {new Date(member.event_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {member.arrival_time ? formatTime(member.arrival_time) : "Not set"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Camera IDs — camera operators & photographers, per day */}
            {cameraCrew.length > 0 && (
              <Card className="mb-6 bg-card p-8">
                <h2 className="mb-6 text-2xl font-bold">Camera IDs</h2>
                <DayTabs
                  dates={festivalDates}
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                  dayNames={briefing.day_names}
                />
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Function
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Camera ID
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isMultiDay
                        ? cameraCrew.filter((m) => m.event_date === selectedDate)
                        : cameraCrew
                      ).map((member) => (
                        <tr key={member.id} className="border-b border-border/50">
                          <td className="px-4 py-3 text-foreground">{member.name}</td>
                          <td className="px-4 py-3 text-foreground">{member.function}</td>
                          <td className="px-4 py-3 text-foreground">
                            {new Date(member.event_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 font-semibold uppercase text-foreground">
                            {normalizeCameraId(member.camera_id) || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        </div>
      </PageTransition>
      {productionId && <EditProductionFAB briefingId={productionId} />}
    </div>
  );
};

export default BriefingView;
